import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, resolveApiUrl, type ChapterRow } from "../api/client";

export function ChapterPage() {
  const { id, chapterNo } = useParams<{ id: string; chapterNo: string }>();
  const runId = id ?? "";
  const n = Number(chapterNo ?? "0");

  const [markdown, setMarkdown] = useState<string | null>(null);
  const [chapterMeta, setChapterMeta] = useState<ChapterRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMarkdown(null);
    setChapterMeta(null);
    setErr(null);
    setIsEditing(false);

    api
      .listChapters(runId)
      .then((res) => {
        if (!cancelled) {
          const match = res.chapters.find((c) => c.chapterNo === n);
          if (match) setChapterMeta(match);
        }
      })
      .catch(() => null);

    api
      .chapterMarkdown(runId, n)
      .then((md) => {
        if (!cancelled) {
          setMarkdown(md);
          setEditedContent(md);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, n]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await api.updateChapter(runId, n, editedContent);
      setMarkdown(editedContent);
      setIsEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: unknown) {
      alert(`Failed to save chapter: ${(e as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const wordCount = (editedContent || "").split(/\s+/).filter(Boolean).length;

  return (
    <article className="space-y-4">
      <div className="flex items-center justify-between text-xs">
        <Link
          to={`/novels/${runId}`}
          className="text-slate-400 hover:text-slate-200"
        >
          ← Back to run
        </Link>
        <div className="flex items-center gap-2">
          {markdown != null && (
            <>
              <button
                onClick={async () => {
                  try {
                    await api.generateChapterThumbnail(runId, n);
                    window.location.reload();
                  } catch (e: unknown) {
                    alert(`Failed to generate thumbnail: ${(e as Error).message}`);
                  }
                }}
                className="rounded bg-slate-800 hover:bg-slate-700 text-amber-300 font-medium px-3 py-1 text-xs border border-slate-700"
              >
                🎨 Scene Thumbnail
              </button>
              <button
                onClick={() => {
                  if (!isEditing) setEditedContent(markdown);
                  setIsEditing((s) => !s);
                }}
                className="rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium px-3 py-1 text-xs border border-slate-700"
              >
                {isEditing ? "Cancel editing" : "Edit chapter"}
              </button>
            </>
          )}
        </div>
      </div>

      <header className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-amber-400 font-medium">
            Chapter {n}
          </div>
        </div>
        {markdown != null && (
          <span className="text-xs font-mono text-slate-400">
            {wordCount} words
          </span>
        )}
      </header>

      {chapterMeta?.thumbnailUrl && (
        <div className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950/40 shadow-lg my-4 max-w-xl flex justify-center items-center">
          <img
            src={resolveApiUrl(chapterMeta.thumbnailUrl)}
            alt={`Chapter ${n} Scene Thumbnail`}
            className="w-full h-auto max-h-[550px] object-contain rounded-xl"
          />
        </div>
      )}

      {saveSuccess && (
        <div className="rounded-md bg-emerald-950 border border-emerald-800 px-4 py-2 text-xs text-emerald-200">
          ✓ Chapter changes saved successfully.
        </div>
      )}

      {err && (
        <div className="rounded-md bg-rose-950 border border-rose-800 px-4 py-3 text-sm text-rose-200">
          {err}
        </div>
      )}

      {markdown == null && !err ? (
        <div className="text-slate-400 text-sm">Loading chapter…</div>
      ) : isEditing ? (
        <div className="space-y-3">
          <div className="flex justify-between items-center text-xs text-slate-400">
            <span>Markdown Editor</span>
            <span className="font-mono">{wordCount} words</span>
          </div>
          <textarea
            rows={20}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="w-full rounded-md bg-slate-950 border border-slate-700 p-4 text-sm font-mono text-slate-100 focus:border-amber-400 focus:outline-none leading-relaxed"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-1.5 text-xs text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-1.5 text-xs shadow transition-colors disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Save Chapter"}
            </button>
          </div>
        </div>
      ) : (
        <div className="prose-bn max-w-none text-slate-200 leading-relaxed space-y-4">
          <ReactMarkdown>{markdown ?? ""}</ReactMarkdown>
        </div>
      )}

      <nav className="flex items-center justify-between pt-6 border-t border-slate-800 text-sm">
        {n > 1 ? (
          <Link
            to={`/novels/${runId}/chapter/${n - 1}`}
            className="text-slate-300 hover:text-amber-300"
          >
            ← Chapter {n - 1}
          </Link>
        ) : (
          <span />
        )}
        <Link
          to={`/novels/${runId}/chapter/${n + 1}`}
          className="text-slate-300 hover:text-amber-300"
        >
          Chapter {n + 1} →
        </Link>
      </nav>
    </article>
  );
}
